import React, { useEffect, useRef } from "react";
import { Box, Heading, Text, VStack, useColorMode } from "@chakra-ui/react";

const TV_SCRIPT_SRC = "https://s3.tradingview.com/tv.js";
const TV_CONTAINER_ID = "study-tradingview-widget";

let tvScriptPromise = null;

const loadTradingViewScript = () => {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.TradingView) return Promise.resolve();
  if (tvScriptPromise) return tvScriptPromise;
  tvScriptPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${TV_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", (e) => reject(e));
      return;
    }
    const script = document.createElement("script");
    script.src = TV_SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = (e) => reject(e);
    document.head.appendChild(script);
  });
  return tvScriptPromise;
};

// The free TradingView embed (tv.js) doesn't expose a reliable
// onSymbolChanged callback to the host page — that API is gated behind the
// licensed Charting Library. We disable in-widget symbol change so all
// ticker switching flows through the page's TickerSearch + watchlist; that
// keeps notes, URL, and the watchlist star indicator in sync.
export default function StudyChart({ ticker }) {
  const { colorMode } = useColorMode();
  const containerRef = useRef(null);

  useEffect(() => {
    if (!ticker) return undefined;
    let cancelled = false;

    loadTradingViewScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.TradingView) return;
        containerRef.current.innerHTML = "";
        // eslint-disable-next-line no-new, new-cap
        new window.TradingView.widget({
          autosize: true,
          symbol: ticker,
          interval: "D",
          timezone: "Etc/UTC",
          theme: colorMode === "light" ? "light" : "dark",
          style: "1",
          locale: "en",
          enable_publishing: false,
          allow_symbol_change: false,
          container_id: TV_CONTAINER_ID,
        });
      })
      .catch(() => {
        // Network-blocked / offline: leave the placeholder text rendered.
      });

    return () => {
      cancelled = true;
    };
  }, [ticker, colorMode]);

  return (
    <VStack align="stretch" spacing={2} h="100%" w="100%">
      <Heading size="sm">Chart</Heading>
      {ticker ? (
        <Box
          id={TV_CONTAINER_ID}
          ref={containerRef}
          flex="1"
          minH="320px"
          borderRadius="md"
          overflow="hidden"
          bg={colorMode === "light" ? "gray.100" : "gray.800"}
        />
      ) : (
        <Box
          flex="1"
          minH="320px"
          borderRadius="md"
          bg={colorMode === "light" ? "gray.100" : "gray.800"}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Text color="gray.500" fontSize="sm">
            Pick a ticker from the watchlist or search above to load a chart.
          </Text>
        </Box>
      )}
    </VStack>
  );
}
